// src/pages/VisitorCheckoutSelect.jsx
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

import {
  getApprovedVisitsByUser,
  concludeVisitById,
} from '../db/visitorcheckoutselectApi';

const VisitorCheckoutSelect = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.uid) {
      fetchVisits();
    }
  }, [user]);

  const fetchVisits = async () => {
    try {
      const data = await getApprovedVisitsByUser(user.uid);
      setVisits(data);
    } catch (error) {
      console.error('Error fetching visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVisit = async (visit) => {
    if (user.role === 'appoperator') {
      try {
        setProcessingId(visit.id);
        await concludeVisitById(visit.id);
        setVisits((prev) => prev.filter((v) => v.id !== visit.id));
        navigate(`/visitor-checkout?visitId=${visit.id}`);
      } catch (error) {
        console.error('Error concluding visit:', error);
      } finally {
        setProcessingId(null);
      }
    } else {
      navigate(`/qr-scanner?visitId=${visit.id}`);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
      <Container maxWidth="md">

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: "2rem",
            fontWeight: 900,
            fontFamily: "'Unbounded', sans-serif",
            color: "#1c1400"
          }}>
            Visitor Checkout
          </Typography>

          <Typography sx={{ color: "#8a7a40" }}>
            Select a visit to proceed with checkout
          </Typography>
        </Box>

        {/* MAIN CARD */}
        <Paper sx={{
          p: 3,
          borderRadius: "16px",
          border: "1px solid #f0d060",
          background: "#ffffff"
        }}>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : visits.length === 0 ? (

            /* EMPTY STATE */
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "#6b7280" }}>
                No approved visits available for checkout.
              </Typography>
            </Box>

          ) : (

            <Stack spacing={2}>
              {visits.map((visit) => (

                <Paper
                  key={visit.id}
                  sx={{
                    p: 3,
                    borderRadius: "14px",
                    border: "1px solid #f0d060",
                    background: "#fffbea",
                    transition: "0.2s",
                    "&:hover": {
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)"
                    }
                  }}
                >

                  {/* TOP ROW */}
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2
                  }}>

                    {/* VISITOR INFO */}
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {visit.visitorName ||
                          visit.visitorDetails?.userName ||
                          visit.visitorDetails?.fullName ||
                          'Unknown Visitor'}
                      </Typography>

                      <Typography sx={{ fontSize: "0.9rem", color: "#6b7280" }}>
                        Host: {
                          visit.hostName ||
                          visit.hostDetails?.userName ||
                          visit.hostDetails?.organization ||
                          'N/A'
                        }
                      </Typography>

                      <Typography sx={{ fontSize: "0.85rem", color: "#6b7280" }}>
                        Purpose: {visit.visitReason || visit.purpose || 'N/A'}
                      </Typography>

                      <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                        Approved: {
                          visit.approvedAt?.seconds
                            ? new Date(visit.approvedAt.seconds * 1000).toLocaleString()
                            : visit.approvalTime?.toDate
                              ? visit.approvalTime.toDate().toLocaleString()
                              : 'Invalid Date'
                        }
                      </Typography>
                    </Box>

                    {/* ACTION */}
                    <Box sx={{ textAlign: "right" }}>
                      <Chip
                        label={visit.status || "approved"}
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          background: "#dcfce7",
                          color: "#166534"
                        }}
                      />

                      <Button
                        disabled={processingId === visit.id}
                        onClick={() => handleSelectVisit(visit)}
                        sx={{
                          minWidth: "160px",
                          height: "42px",
                          borderRadius: "10px",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          letterSpacing: "0.5px",
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",

                          background:
                            user.role === "appoperator" ? "#16a34a" : "#1c1400",

                          color:
                            user.role === "appoperator" ? "#ffffff" : "#fde047",

                          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",

                          "&:hover": {
                            background:
                              user.role === "appoperator" ? "#15803d" : "#f59e0b",
                            color:
                              user.role === "appoperator" ? "#ffffff" : "#1c1400",
                          },

                          "&:disabled": {
                            background: "#d1d5db",
                            color: "#6b7280"
                          }
                        }}
                      >
                        {processingId === visit.id ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : user.role === "appoperator" ? (
                          "✔ Conclude Visit"
                        ) : (
                          "Scan QR →"
                        )}
                      </Button>
                    </Box>

                  </Box>

                </Paper>

              ))}
            </Stack>
          )}
        </Paper>

      </Container>
    </Box>
  );
};

export default VisitorCheckoutSelect;
